import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { shipmentsService } from '../services/shipmentsService'
import LocationModal from '../components/LocationModal.jsx'
import ShipmentLogsDrawer from '../components/ShipmentLogsDrawer.jsx'
import { getShipmentRequester, getShipmentRequesterName } from '../utils/shipmentHelpers'

// Converts numbers to words (simple PKR format)
const numberToWords = (num) => {
    if (num === 0) return "Zero Only";

    const a = [
        "", "One", "Two", "Three", "Four", "Five", "Six", "Seven",
        "Eight", "Nine", "Ten", "Eleven", "Twelve", "Thirteen",
        "Fourteen", "Fifteen", "Sixteen", "Seventeen", "Eighteen", "Nineteen"
    ];
    const b = [
        "", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"
    ];

    const numToWords = (n) => {
        if (n < 20) return a[n];
        if (n < 100) return b[Math.floor(n / 10)] + (n % 10 ? " " + a[n % 10] : "");
        if (n < 1000)
            return a[Math.floor(n / 100)] + " Hundred" + (n % 100 ? " " + numToWords(n % 100) : "");
        if (n < 1000000)
            return numToWords(Math.floor(n / 1000)) + " Thousand" + (n % 1000 ? " " + numToWords(n % 1000) : "");
        if (n < 1000000000)
            return numToWords(Math.floor(n / 1000000)) + " Million" + (n % 1000000 ? " " + numToWords(n % 1000000) : "");
        return n.toString();
    };

    return numToWords(num) + " Only";
};

// Helper function to get platform display value
const getPlatformDisplay = (shipmentData) => {
    // Check multiple possible field names from backend
    const platformValue = shipmentData?.platform ||
        shipmentData?.source ||
        shipmentData?.submittedFrom ||
        shipmentData?.bookingPlatform ||
        shipmentData?.bookingSource ||
        shipmentData?.submissionPlatform;

    if (!platformValue || platformValue === null || platformValue === undefined) {
        return { text: 'N/A', isMobile: false };
    }

    const platformLower = platformValue.toString().toLowerCase().trim();

    // Check for mobile app variations
    const isMobile = platformLower === 'mobile' ||
        platformLower === 'app' ||
        platformLower === 'cargo360-client-app' ||
        platformLower === 'android' ||
        platformLower === 'ios';

    // Check for web portal variations
    const isWeb = platformLower === 'web' ||
        platformLower === 'portal' ||
        platformLower === 'cargo360-client-portal' ||
        platformLower === 'website';

    if (isMobile) return { text: 'Mobile', isMobile: true };
    if (isWeb) return { text: 'Web', isMobile: false };

    // If value exists but doesn't match known patterns, display as-is
    return { text: platformValue, isMobile: false };
};

const OrderDetailPage = () => {
    const { orderId } = useParams()
    const navigate = useNavigate()
    const [shipmentData, setShipmentData] = useState(null)
    const [loading, setLoading] = useState(true)
    const [showLocationModal, setShowLocationModal] = useState(false)
    const [showLogsDrawer, setShowLogsDrawer] = useState(false)

    useEffect(() => {
        fetchShipmentDetails()
    }, [orderId])

    const fetchShipmentDetails = async () => {
        setLoading(true)
        try {
            // Extract shipment ID from order ID (remove C360-PK- prefix if present)
            const shipmentId = orderId.replace('C360-PK-', '')

            const result = await shipmentsService.getShipmentById(shipmentId)

            if (result.success) {
                // The service already handles the response structure, so result.data is the shipment object
                setShipmentData(result.data)
            } else {
                toast.error(result.message || 'Failed to fetch shipment details')
            }
        } catch (error) {
            toast.error('An error occurred while fetching shipment details')
        } finally {
            setLoading(false)
        }
    }

    const handleAcceptDiscountRequest = async (discountRequestId, action) => {
        const result = await shipmentsService.decideDiscountRequest(discountRequestId, action)
        if (result.success) {
            toast.success(result.message || 'Decision recorded')
            fetchShipmentDetails() // Refresh the data
        } else {
            toast.error(result.error || result.message || 'Failed to decide discount request')
        }
    }

    if (loading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
                <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-4 border-[#3498db] border-t-transparent rounded-full animate-spin" />
                    <p className="text-[#7f8c8d] text-sm">Loading shipment details…</p>
                </div>
            </div>
        )
    }

    if (!shipmentData) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-[#f8f9fa]">
                <p className="text-[#e74c3c]">Shipment not found</p>
            </div>
        )
    }

    const isTrackable = shipmentData.status === 'in_transit' || shipmentData.status === 'picked_up'
    const platformDisplay = getPlatformDisplay(shipmentData)
    const requester = getShipmentRequester(shipmentData)

    const statusBadge = (status) => {
        const map = {
            delivered:  'bg-[#eafaf1] text-[#27ae60]',
            accepted:   'bg-[#eaf4fb] text-[#3498db]',
            in_transit: 'bg-[#fef5e7] text-[#e67e22]',
            picked_up:  'bg-[#fef5e7] text-[#e67e22]',
            cancelled:  'bg-[#fdedec] text-[#e74c3c]',
        }
        return map[status] || 'bg-[#f8f9fa] text-[#7f8c8d]'
    }

    const Field = ({ label, value }) => (
        <div>
            <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">{label}</p>
            <p className="text-sm text-[#2c3e50]">{value || '—'}</p>
        </div>
    )

    const Section = ({ title, children }) => (
        <div className="bg-white border border-[#e9ecef] rounded-lg p-5">
            <h3 className="text-sm font-semibold text-[#2c3e50] pb-3 mb-4 border-b border-[#e9ecef]">{title}</h3>
            {children}
        </div>
    )

    return (
        <div className="px-[20px] md:px-[40px] py-[30px] min-h-screen bg-[#f8f9fa]">
            {/* Page Header */}
            <div className="flex items-center justify-between pb-5 mb-6 border-b border-[#e9ecef]">
                <div className="flex items-center gap-3">
                    <button onClick={() => navigate(-1)} className="text-[#7f8c8d] hover:text-[#2c3e50] text-sm flex items-center gap-1 transition-colors">
                        ← Back
                    </button>
                    <h1 className="text-[1.8rem] font-semibold text-[#2c3e50]">Shipment Details</h1>
                </div>
                {isTrackable && (
                    <button
                        onClick={() => setShowLocationModal(true)}
                        className="bg-[#3498db] hover:bg-[#2980b9] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors"
                    >
                        View Location
                    </button>
                )}
            </div>

            <div className="space-y-5">
                {/* Basic Info Banner */}
                <div className="bg-white border border-[#e9ecef] rounded-lg p-5 grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="Shipment ID" value={`C360-PK-${shipmentData.id}`} />
                    <Field label="Customer" value={getShipmentRequesterName(shipmentData)} />
                    <div>
                        <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">Status</p>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${statusBadge(shipmentData.status)}`}>
                            {shipmentData.status?.replace('_', ' ').toUpperCase() || 'PENDING'}
                        </span>
                    </div>
                    <div>
                        <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">Platform</p>
                        <span className={`px-2 py-1 rounded text-xs font-semibold ${platformDisplay.isMobile ? 'bg-[#f0e6ff] text-[#8e44ad]' : 'bg-[#eaf4fb] text-[#3498db]'}`}>
                            {platformDisplay.text}
                        </span>
                    </div>
                </div>

                {/* Location */}
                <Section title="Location Details">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Pickup Location" value={shipmentData.pickupLocation} />
                        <Field label="Drop Location" value={shipmentData.dropLocation} />
                    </div>
                </Section>

                {/* Customer */}
                <Section title="Customer Details">
                    <div className="grid grid-cols-2 gap-4">
                        <Field label="Name" value={requester.name} />
                        <Field label="Phone" value={requester.phone} />
                        <Field label="Company" value={requester.company} />
                        <Field label="Email" value={requester.email} />
                    </div>
                </Section>

                {/* Cargo */}
                <Section title="Cargo Information">
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                        <Field label="Cargo Type" value={shipmentData.cargoType} />
                        <Field label="Vehicle Type" value={shipmentData.vehicleType} />
                        <Field label="Weight" value={shipmentData.cargoWeight ? `${shipmentData.cargoWeight} kg` : null} />
                        <Field label="Size" value={shipmentData.cargoSize} />
                        <Field label="Estimated Budget" value={shipmentData.budget?.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',')} />
                    </div>

                    {/* Discount Request */}
                    {shipmentData.DiscountRequest && (
                        <div className="mt-4 p-4 bg-[#fef9e7] border border-[#f39c12]/30 rounded-lg max-w-md">
                            <p className="text-xs font-semibold text-[#7f8c8d] uppercase tracking-wide mb-3">Discount Request</p>
                            <p className="text-sm text-[#2c3e50] mb-1">
                                Client requested discount of: <span className="font-semibold">PKR {shipmentData.DiscountRequest.requestAmount}</span>
                            </p>
                            <p className="text-sm text-[#7f8c8d] mb-2">
                                Status: <span className={`font-semibold ${shipmentData.DiscountRequest.status === 'pending' ? 'text-[#e67e22]' : 'text-[#2c3e50]'}`}>{shipmentData.DiscountRequest.status}</span>
                            </p>
                            {shipmentData.budget && (
                                <>
                                    <p className="text-sm font-semibold text-[#2c3e50]">
                                        Total: PKR {shipmentData.totalAmount
                                            ? (parseFloat(shipmentData.budget) - parseFloat(shipmentData.totalAmount)).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                            : parseFloat(shipmentData.budget).toLocaleString('en-US', { minimumFractionDigits: 2 })
                                        }
                                    </p>
                                    <p className="text-xs text-[#7f8c8d] mt-1">
                                        {numberToWords(shipmentData.totalAmount
                                            ? parseFloat(shipmentData.budget) - parseFloat(shipmentData.totalAmount)
                                            : parseFloat(shipmentData.budget)
                                        )}
                                    </p>
                                </>
                            )}
                            {shipmentData.DiscountRequest.status === 'pending' && (
                                <div className="flex gap-2 mt-3">
                                    <button
                                        onClick={() => handleAcceptDiscountRequest(shipmentData.DiscountRequest.id, 'accept')}
                                        className="px-4 py-2 bg-[#27ae60] hover:bg-[#219a52] text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Accept
                                    </button>
                                    <button
                                        onClick={() => handleAcceptDiscountRequest(shipmentData.DiscountRequest.id, 'reject')}
                                        className="px-4 py-2 bg-[#e74c3c] hover:bg-[#c0392b] text-white rounded-lg text-sm font-medium transition-colors"
                                    >
                                        Reject
                                    </button>
                                </div>
                            )}
                        </div>
                    )}

                    {shipmentData.description && (
                        <div className="mt-4">
                            <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-1">Description</p>
                            <p className="text-sm text-[#2c3e50]">{shipmentData.description}</p>
                        </div>
                    )}
                </Section>

                {/* Assignment */}
                {(shipmentData.Trucker || shipmentData.Driver) && (
                    <Section title="Assignment Details">
                        <div className="grid grid-cols-2 gap-4">
                            {shipmentData.Trucker && (
                                <div className="p-4 bg-[#f8f9fa] border border-[#e9ecef] rounded-lg">
                                    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Assigned Broker</p>
                                    <p className="text-sm font-semibold text-[#2c3e50]">{shipmentData.Trucker.name}</p>
                                    <p className="text-xs text-[#7f8c8d] mt-1">{shipmentData.Trucker.email}</p>
                                    {shipmentData.Trucker.Vehicles?.length > 0 && (
                                        <div className="mt-3 pt-3 border-t border-[#e9ecef]">
                                            <p className="text-xs font-semibold text-[#7f8c8d] mb-1">Fleet ({shipmentData.Trucker.Vehicles.length})</p>
                                            {shipmentData.Trucker.Vehicles.map((v, i) => (
                                                <p key={i} className="text-xs text-[#2c3e50]">
                                                    <span className="font-medium">{v.vehicleType}</span> — {v.registrationNumber}
                                                </p>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            {shipmentData.Driver && (
                                <div className="p-4 bg-[#f8f9fa] border border-[#e9ecef] rounded-lg">
                                    <p className="text-xs font-medium text-[#7f8c8d] uppercase tracking-wide mb-2">Assigned Driver</p>
                                    <p className="text-sm font-semibold text-[#2c3e50]">{shipmentData.Driver.name}</p>
                                    <p className="text-xs text-[#7f8c8d] mt-1">{shipmentData.Driver.email}</p>
                                    {shipmentData.Driver.phone && <p className="text-xs text-[#7f8c8d]">{shipmentData.Driver.phone}</p>}
                                </div>
                            )}
                        </div>
                    </Section>
                )}

                {/* Audit Log */}
                {shipmentData._logsCount > 0 && (
                    <Section title="Audit Log">
                        <div className="flex items-center justify-between">
                            <p className="text-sm text-[#2c3e50]">
                                Total changes: <span className="font-semibold text-[#3498db]">{shipmentData._logsCount}</span>
                            </p>
                            <button
                                onClick={() => setShowLogsDrawer(true)}
                                className="px-4 py-2 bg-[#3498db] hover:bg-[#2980b9] text-white rounded-lg text-sm font-medium transition-colors"
                            >
                                View Changes
                            </button>
                        </div>
                    </Section>
                )}

                {/* Timeline */}
                {(shipmentData.createdAt || shipmentData.updatedAt) && (
                    <Section title="Timeline">
                        <div className="grid grid-cols-2 gap-4">
                            <Field label="Created At" value={shipmentData.createdAt ? new Date(shipmentData.createdAt).toLocaleString() : null} />
                            <Field label="Last Updated" value={shipmentData.updatedAt ? new Date(shipmentData.updatedAt).toLocaleString() : null} />
                        </div>
                    </Section>
                )}
            </div>

            {showLocationModal && (
                <LocationModal
                    shipment={{
                        orderId: `C360-PK-${shipmentData.id}`,
                        shipmentId: shipmentData.id,
                        customer: getShipmentRequesterName(shipmentData),
                        status: shipmentData.status,
                        shipmentData: shipmentData
                    }}
                    onClose={() => setShowLocationModal(false)}
                />
            )}

            <ShipmentLogsDrawer
                open={showLogsDrawer}
                onClose={() => setShowLogsDrawer(false)}
                shipmentId={shipmentData?.id}
            />
        </div>
    )
}

export default OrderDetailPage

